// Radius search: which postcode districts a scan searches around each
// business location. Used by the search page and (next) the CSV upload.
//
// Each business location gives exactly 5 postcodes: its own district, plus
// one in each quarter (N, E, S, W) at a different distance across the radius
// - one near (~20% of the radius), two mid (~50%), one far (~80%). In each
// quarter's band the most populated district is picked (most live postcodes,
// from postcode_districts). Shuffle turns the near/mid/far pattern round a
// quarter and steps one down the population list in each band.
// No guessing or randomness: the same inputs always give the same picks.
//
// districts: [{ outcode, lat, lng, live_postcodes, area }] (postcode_districts).

export const RADIUS_MILES = [5, 10, 15, 25, 50, 100];
export const PICKS_PER_LOCATION = 5;

export const QUARTERS = ["N", "E", "S", "W"];
// Each band as a share of the radius: [from, to). The centre's own district
// is never one of them.
export const BANDS = {
  near: [0, 0.35],
  mid: [0.35, 0.65],
  far: [0.65, 1.0001],
};
// Which band each quarter gets before any Shuffle: N near, E mid, S far, W mid.
const BAND_PATTERN = ["near", "mid", "far", "mid"];
// An empty band (coast, edge of the UK) falls back to the nearest band in the same quarter.
const FALLBACK = { near: ["mid", "far"], mid: ["near", "far"], far: ["mid", "near"] };

const EARTH_RADIUS_MILES = 3958.8;
const OUTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/;

const toRad = d => (d * Math.PI) / 180;

export function distanceMiles(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(h));
}

// Compass bearing from a to b, 0-360 (0 = north).
export function bearing(a, b) {
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// N = 315-45, E = 45-135, S = 135-225, W = 225-315.
export function quarterOf(degrees) {
  return QUARTERS[Math.floor(((degrees + 45) % 360) / 90)];
}

// "S1 2AB" -> "S1", "s1" -> "S1", "S" -> null.
export function outcodeOf(postcode) {
  const code = String(postcode || "").trim().toUpperCase().split(/\s+/)[0];
  if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(code)) return code.slice(0, -3);
  return OUTCODE_RE.test(code) ? code : null;
}

// The band each quarter gets after `shuffle` Shuffles: { N: "near", ... }.
export function bandsForShuffle(shuffle = 0) {
  const out = {};
  QUARTERS.forEach((q, i) => { out[q] = BAND_PATTERN[(((i - shuffle) % 4) + 4) % 4]; });
  return out;
}

// One postcode to search, from a district row.
function fromDistrict(d, extra) {
  return { postcode: d.outcode, lat: d.lat, lng: d.lng, places: d.area ? [d.area] : [], ...extra };
}

// The picks for every business location.
// centres: [{ postcode, lat, lng, radiusMiles? }] - the business locations,
//   each with its own radius if given, otherwise radiusMiles.
// radiusMiles: null or 0 = no radius, just each location's own district.
// shuffle: 0, 1, 2 ... (how many times Shuffle was pressed).
// A district is only picked once: where circles overlap, the next one in that
// band is used instead, so each location still gets 5 if there are enough.
// Returns { picks, gaps }:
//   picks: [{ postcode, lat, lng, places, centre, quarter, band, isCentre }]
//     (centre = the business location's postcode)
//   gaps: [{ centre, quarter }] - quarters with no district in any band
export function pickRadiusPostcodes({ districts, centres, radiusMiles = null, shuffle = 0 }) {
  const byOutcode = new Map((districts || []).map(d => [d.outcode, d]));
  const taken = new Set();
  const picks = [];
  const gaps = [];
  const bands = bandsForShuffle(shuffle);

  // Every location's own district first, so another circle can't take it.
  const located = [];
  for (const c of centres || []) {
    const own = outcodeOf(c.postcode);
    if (!own) continue;
    const row = byOutcode.get(own);
    const lat = row ? row.lat : c.lat;
    const lng = row ? row.lng : c.lng;
    if (lat == null || lng == null) continue;
    located.push({ centre: c, lat, lng, radius: c.radiusMiles !== undefined ? c.radiusMiles : radiusMiles });
    if (taken.has(own)) continue;
    taken.add(own);
    picks.push(row
      ? fromDistrict(row, { centre: c.postcode, quarter: null, band: null, isCentre: true })
      : { postcode: own, lat, lng, places: c.places || [], centre: c.postcode, quarter: null, band: null, isCentre: true });
  }

  for (const { centre, lat, lng, radius } of located) {
    if (!radius) continue;
    // Districts in this circle, sorted into quarter and band.
    const sorted = {};
    for (const d of districts || []) {
      const miles = distanceMiles({ lat, lng }, d);
      const share = miles / radius;
      if (miles === 0 || share >= BANDS.far[1]) continue;
      const band = Object.keys(BANDS).find(b => share >= BANDS[b][0] && share < BANDS[b][1]);
      const key = `${quarterOf(bearing({ lat, lng }, d))}:${band}`;
      (sorted[key] || (sorted[key] = [])).push(d);
    }
    // Most live postcodes first; A-Z on a tie.
    for (const list of Object.values(sorted)) {
      list.sort((a, b) => (b.live_postcodes || 0) - (a.live_postcodes || 0) || a.outcode.localeCompare(b.outcode));
    }

    for (const quarter of QUARTERS) {
      const wanted = bands[quarter];
      let picked = null;
      for (const band of [wanted, ...FALLBACK[wanted]]) {
        const free = (sorted[`${quarter}:${band}`] || []).filter(d => !taken.has(d.outcode));
        if (free.length === 0) continue;
        picked = fromDistrict(free[shuffle % free.length], { centre: centre.postcode, quarter, band, isCentre: false });
        break;
      }
      if (!picked) { gaps.push({ centre: centre.postcode, quarter }); continue; }
      taken.add(picked.postcode);
      picks.push(picked);
    }
  }

  return { picks, gaps };
}

// The postcodes a scan searches: the radius picks (less any the user removed),
// then the extra postcodes on top. An extra that's already in the list is
// skipped, not added twice.
// removed: postcodes taken off with ×. Removing one doesn't replace it.
// Returns { postcodes, added, skipped } - added / skipped are the extras.
export function combineLocations({ picks, removed = [], extras = [] }) {
  const gone = new Set(removed);
  const postcodes = (picks || []).filter(p => !gone.has(p.postcode));
  const seen = new Set(postcodes.map(p => p.postcode));
  const added = [];
  const skipped = [];
  for (const e of extras || []) {
    if (seen.has(e.postcode)) { skipped.push(e); continue; }
    seen.add(e.postcode);
    added.push(e);
  }
  return { postcodes: [...postcodes, ...added.map(e => ({ ...e, isExtra: true }))], added, skipped };
}
